/**
 * ledger — Ghi Thu/Chi NGUYÊN TỬ.
 *
 * Mỗi lệnh là MỘT Firestore transaction: bản ghi Thu/Chi + số dư ví +
 * dòng lịch sử `transactions` cùng thành công hoặc cùng thất bại. Thay cho
 * chuỗi 3–4 lệnh rời của bản cũ (mất mạng giữa chừng → số dư lệch im lặng;
 * kết quả trừ số dư không được kiểm tra).
 *
 * Dữ liệu không đổi: cùng đường dẫn, cùng field, cùng quy ước dấu, cùng
 * dạng dòng `transactions` — bản cũ và bản mới dùng chung được.
 * Chữ ký trả về giữ như service cũ: { success, id?, error?, warnings? }.
 */
import {
  getFirestore, collection, doc, runTransaction, Timestamp
} from 'firebase/firestore';
import { APP_ID } from '../config/constants';
import { isQuotaError, QUOTA_MESSAGE } from './errors';
import {
  planChanges, checkChange, vnd,
  type Entry, type EntryKind, type Item, type SourceState, type WalletField
} from './effects';

export class LedgerError extends Error {}

export interface LedgerResult {
  success: boolean;
  id?: string;
  count?: number;
  error?: string;
  warnings?: string[];
  /** Số khoản dự kiến được mở lại khi xoá giao dịch sinh từ chúng */
  reopened?: number;
}

const base = (userId: string) => ['artifacts', APP_ID, 'users', userId] as const;
const COLLECTION: Record<EntryKind, string> = { income: 'incomes', expense: 'expenses' };
const LABEL: Record<EntryKind, string> = { income: 'Thu nhập', expense: 'Chi tiêu' };

// Hàm kiểu "any" ở biên Firestore: SDK là JS, dữ liệu là schema-less
type Data = Record<string, any>;

const toTimestamp = (v: unknown): Timestamp | null => {
  if (!v) return null;
  if (typeof v === 'string') return Timestamp.fromDate(new Date(v));
  if (v instanceof Date) return Timestamp.fromDate(v);
  if (typeof (v as Timestamp).toDate === 'function') return v as Timestamp;
  return null;
};

/** Firestore từ chối giá trị `undefined` — bỏ các trường rỗng trước khi ghi (khoản dự kiến sinh từ mẫu không có nhiều trường tuỳ chọn như incomeSourceId). */
export const stripUndefined = (data: Data): Data => Object.fromEntries(Object.entries(data).filter(([, v]) => v !== undefined));

/** Đảm bảo year/month luôn có (báo cáo xếp kỳ theo hai field này). */
const withPeriod = (data: Data): Data => {
  const ts = data.transactionDate as Timestamp | undefined;
  if (ts && (data.year == null || data.month == null)) {
    const d = ts.toDate();
    return { ...data, year: data.year ?? d.getFullYear(), month: data.month ?? d.getMonth() + 1 };
  }
  return data;
};

interface Op {
  kind: EntryKind;
  op: 'add' | 'update' | 'delete';
  id?: string;
  data?: Data;      // add: toàn bộ; update: phần thay đổi
  /** Xác nhận từ dự chi/dự thu: đọc + khoá bản ghi kế hoạch trong CÙNG transaction */
  planned?: { collection: 'plannedExpenses' | 'plannedIncomes'; id: string };
  /** Dựng bản ghi thực từ dữ liệu kế hoạch (chỉ dùng cùng `planned`) */
  build?: (planned: Data) => Data;
  /** Xoá: khoản dự kiến đã sinh ra bản ghi này (cho dữ liệu cũ chưa có liên kết ngược) — sẽ được mở lại */
  reopen?: PlannedLink;
}

export interface PlannedLink { collection: 'plannedExpenses' | 'plannedIncomes'; id: string }
const PLAN_COLLECTIONS = ['plannedExpenses', 'plannedIncomes'];

/**
 * Lõi dùng chung: đọc → tính → kiểm tra chính sách → ghi, trong một transaction.
 * `ops` nhiều phần tử chỉ dùng cho xoá hàng loạt (gộp độ lệch theo ví).
 */
const run = async (userId: string, ops: Op[]): Promise<{ ids: string[]; warnings: string[]; reopened: number }> => {
  const db = getFirestore();
  const [a, b, c, d] = base(userId);
  const warnings: string[] = [];
  const ids: string[] = [];
  let reopened = 0;

  await runTransaction(db, async (tx) => {
    warnings.length = 0;
    ids.length = 0;
    reopened = 0;
    const now = Timestamp.now();

    // ── 1. ĐỌC (mọi lệnh đọc phải đứng trước lệnh ghi) ──
    const plan: Array<{ o: Op; ref: any; old: Data | null; next: Data | null; patch: Data | null; plannedRef: any; reopenRef: any }> = [];
    for (const o of ops) {
      const ref = o.op === 'add'
        ? doc(collection(db, a, b, c, d, COLLECTION[o.kind]))
        : doc(db, a, b, c, d, COLLECTION[o.kind], o.id!);
      let plannedRef: any = null;
      let input: Data | undefined = o.data;
      if (o.planned) {
        plannedRef = doc(db, a, b, c, d, o.planned.collection, o.planned.id);
        const ps = await tx.get(plannedRef);
        if (!ps.exists()) throw new LedgerError('Không tìm thấy khoản dự kiến — có thể đã bị xoá.');
        const pd = ps.data() as Data;
        if (pd.status === 'confirmed') throw new LedgerError('Khoản dự kiến này đã được xác nhận trước đó.');
        if (pd.status === 'cancelled') throw new LedgerError('Khoản dự kiến này đã bị huỷ — không thể xác nhận.');
        if (o.build) input = o.build(pd);
      }
      let old: Data | null = null;
      if (o.op !== 'add') {
        const snap = await tx.get(ref);
        if (!snap.exists()) {
          if (ops.length > 1) continue; // xoá hàng loạt: bỏ qua bản ghi đã mất
          throw new LedgerError('Không tìm thấy bản ghi — có thể đã bị xoá ở nơi khác.');
        }
        old = snap.data() as Data;
      }
      // Xoá bản ghi sinh từ khoản dự kiến → mở lại khoản đó (chỉ khi liên kết còn khớp), cùng transaction
      let reopenRef: any = null;
      if (o.op === 'delete' && old) {
        const link: PlannedLink | undefined = old.plannedId ? { collection: old.plannedCollection, id: old.plannedId } : o.reopen;
        if (link && PLAN_COLLECTIONS.includes(link.collection)) {
          const r = doc(db, a, b, c, d, link.collection, link.id);
          const ps = await tx.get(r);
          const key = o.kind === 'income' ? 'actualIncomeId' : 'actualExpenseId';
          if (ps.exists() && (ps.data() as Data).status === 'confirmed' && (ps.data() as Data)[key] === ref.id) reopenRef = r;
        }
      }
      let next: Data | null = null;
      let patch: Data | null = null;
      if (o.op === 'add') {
        const td = toTimestamp(input?.transactionDate) ?? now;
        next = withPeriod(stripUndefined({ ...input, transactionDate: td, createdAt: now, updatedAt: now, ...(o.planned ? { plannedId: o.planned.id, plannedCollection: o.planned.collection } : {}) }));
      } else if (o.op === 'update') {
        patch = stripUndefined({ ...o.data, updatedAt: now });
        if (o.data?.transactionDate) patch.transactionDate = toTimestamp(o.data.transactionDate);
        next = withPeriod({ ...old, ...patch });
        // Đổi ngày mà không truyền year/month → tính lại để báo cáo xếp đúng kỳ
        if (patch.transactionDate && o.data?.year == null && o.data?.month == null) {
          const d = (patch.transactionDate as Timestamp).toDate();
          next = { ...next, year: d.getFullYear(), month: d.getMonth() + 1 };
        }
        if (next.year !== old?.year) patch.year = next.year;
        if (next.month !== old?.month) patch.month = next.month;
      }
      plan.push({ o, ref, old, next, patch, plannedRef, reopenRef });
      ids.push(ref.id);
    }

    const walletIds = new Set<string>();
    for (const p of plan) {
      if (p.old?.walletId) walletIds.add(p.old.walletId);
      if (p.next?.walletId) walletIds.add(p.next.walletId);
    }
    const sources = new Map<string, { ref: any; data: SourceState | null }>();
    for (const id of walletIds) {
      const ref = doc(db, a, b, c, d, 'sources', id);
      const snap = await tx.get(ref);
      sources.set(id, { ref, data: snap.exists() ? (snap.data() as SourceState) : null });
    }
    const isCredit = (id: string) => sources.get(id)?.data?.type === 'credit';

    // ── 2. TÍNH + KIỂM TRA CHÍNH SÁCH ──
    const items: Item[] = plan.map(p => ({ kind: p.o.kind, old: p.old as Entry | null, next: p.next as Entry | null }));
    const changes = planChanges(items, isCredit);
    const logs: Data[] = [];
    const written = new Map<string, Data>(); // chỉ các field THẬT SỰ đổi (không ghi undefined)
    const single = plan.length === 1 ? plan[0] : null;

    for (const ch of changes) {
      const src = sources.get(ch.walletId);
      if (!src?.data) {
        // Ví đã bị xoá: chỉ chấp nhận khi KHÔNG ghi mới vào ví đó (xoá/đảo)
        const writesInto = plan.some(p => p.next?.walletId === ch.walletId);
        if (writesInto) throw new LedgerError('Không tìm thấy ví đã chọn — hãy chọn ví khác.');
        warnings.push('Ví gốc của khoản này đã bị xoá nên không có số dư nào để cập nhật.');
        continue;
      }
      const res = checkChange(src.data, ch.field as WalletField, ch.delta);
      if (!res.ok) throw new LedgerError(res.error);
      if (res.warning) warnings.push(res.warning);
      (src.data as Data)[ch.field] = res.next;
      written.set(ch.walletId, { ...written.get(ch.walletId), [ch.field]: res.next });
      logs.push({
        sourceId: ch.walletId,
        amount: ch.delta,
        type: ch.txType,
        relatedId: single?.ref.id ?? 'bulk',
        description: describe(plan.length, single, ch.field),
        timestamp: now,
        createdAt: now
      });
    }

    // ── 3. GHI ──
    for (const p of plan) {
      if (p.o.op === 'delete') {
        tx.delete(p.ref);
        if (p.reopenRef) {
          tx.update(p.reopenRef, {
            status: 'pending', confirmedAt: null, confirmedBy: null,
            [p.o.kind === 'income' ? 'actualIncomeId' : 'actualExpenseId']: null, reopenedAt: now, updatedAt: now
          });
          reopened++;
        }
      }
      else if (p.o.op === 'add') {
        tx.set(p.ref, p.next!);
        if (p.plannedRef) {
          tx.update(p.plannedRef, {
            status: 'confirmed', confirmedAt: now, confirmedBy: userId, updatedAt: now, confirmedAmount: Number((p.next as Data).amount) || 0,
            [p.o.kind === 'income' ? 'actualIncomeId' : 'actualExpenseId']: p.ref.id
          });
        }
      }
      else tx.update(p.ref, p.patch!);
    }
    for (const [id, fields] of written) {
      tx.update(sources.get(id)!.ref, { ...fields, updatedAt: now });
    }
    for (const log of logs) tx.set(doc(collection(db, a, b, c, d, 'transactions')), log);
  });

  return { ids, warnings, reopened };
};

const describe = (
  n: number,
  p: { o: Op; old: Data | null; next: Data | null } | null,
  field: WalletField
): string => {
  if (!p) return `Xoá hàng loạt (${n} khoản)`;
  const e = p.next ?? p.old ?? {};
  const cat = e.category || e.description || 'Không xác định';
  const verb = p.o.op === 'add' ? '' : p.o.op === 'update' ? 'Cập nhật: ' : 'Xoá: ';
  const label = p.o.kind === 'income' ? LABEL.income : (e.isRefund ? 'Hoàn tiền' : LABEL.expense);
  return `${verb}${label}${field === 'usedCredit' ? ' (thẻ)' : ''}: ${cat}`;
};

const guard = async (fn: () => Promise<Partial<LedgerResult>>): Promise<LedgerResult> => {
  try {
    return { success: true, ...(await fn()) };
  } catch (e) {
    if (e instanceof LedgerError) return { success: false, error: e.message };
    if (isQuotaError(e)) return { success: false, error: QUOTA_MESSAGE };
    console.error('Ledger error:', e);
    return { success: false, error: `Không ghi được (chưa có gì thay đổi): ${(e as Error).message}` };
  }
};

const single = (kind: EntryKind, op: Op['op']) =>
  (userId: string, idOrData: string | Data, arg?: Data | { reopen?: PlannedLink }) =>
    guard(async () => {
      const o: Op = op === 'add'
        ? { kind, op, data: idOrData as Data }
        : op === 'delete'
          ? { kind, op, id: idOrData as string, reopen: (arg as { reopen?: PlannedLink } | undefined)?.reopen }
          : { kind, op, id: idOrData as string, data: arg as Data };
      const { ids, warnings, reopened } = await run(userId, [o]);
      return { id: ids[0], warnings, reopened };
    });

export const addIncome = single('income', 'add') as (userId: string, data: Data) => Promise<LedgerResult>;
export const updateIncome = single('income', 'update') as (userId: string, id: string, updates: Data) => Promise<LedgerResult>;
export const deleteIncome = single('income', 'delete') as (userId: string, id: string, opts?: { reopen?: PlannedLink }) => Promise<LedgerResult>;
export const addExpense = single('expense', 'add') as (userId: string, data: Data) => Promise<LedgerResult>;
export const updateExpense = single('expense', 'update') as (userId: string, id: string, updates: Data) => Promise<LedgerResult>;
export const deleteExpense = single('expense', 'delete') as (userId: string, id: string, opts?: { reopen?: PlannedLink }) => Promise<LedgerResult>;

/** Xoá hàng loạt: chia lô 120 khoản/transaction (giới hạn 500 lệnh ghi của Firestore). */
const bulkDelete = (kind: EntryKind) => (userId: string, ids: string[], reopen: Record<string, PlannedLink> = {}): Promise<LedgerResult> =>
  guard(async () => {
    const warnings: string[] = [];
    let reopened = 0;
    for (let i = 0; i < ids.length; i += 120) {
      const r = await run(userId, ids.slice(i, i + 120).map(id => ({ kind, op: 'delete' as const, id, reopen: reopen[id] })));
      warnings.push(...r.warnings);
      reopened += r.reopened;
    }
    return { count: ids.length, warnings, reopened };
  });
export const bulkDeleteExpenses = bulkDelete('expense');
export const bulkDeleteIncomes = bulkDelete('income');

export { vnd };

// ───────── Khoản vay ─────────

export interface LoanInput {
  lender: string;
  principal: number;
  interestRate?: number;
  term?: number;
  dueDate?: string | null;
  type?: string;
  note?: string;
  repaymentMethod?: 'annuity' | 'flexible';
  monthlyPayment?: number | null;
  /** Ví nhận tiền vay (tuỳ chọn). Có ví → tiền vào ví NGAY trong cùng giao dịch, KHÔNG ghi là thu nhập. */
  receiveWalletId?: string;
  /** Vay/trả góp trên thẻ tín dụng: số tiền này ĐÃ nằm trong dư nợ thẻ, không cộng thêm vào tài sản ròng */
  creditCardId?: string;
  /** Số tiền thực nhận (mặc định = gốc; thường thấp hơn nếu bị trừ phí) */
  actualReceived?: number;
}

/**
 * Tạo khoản vay (+ giải ngân vào ví) NGUYÊN TỬ: khoản vay, số dư ví và dòng lịch sử
 * `loan_disbursement` cùng thành công/thất bại. Cách ghi đúng cho tiền vay: nợ tăng,
 * tiền vào ví, KHÔNG phải thu nhập (xem isLoanProceeds trong finance-core).
 */
export const addLoan = (userId: string, input: LoanInput): Promise<LedgerResult> =>
  guard(async () => {
    const lender = input.lender?.trim();
    const principal = Number(input.principal);
    if (!lender) throw new LedgerError('Nhập tên bên cho vay.');
    if (!(principal > 0)) throw new LedgerError('Số tiền vay phải lớn hơn 0.');
    const received = input.receiveWalletId ? Number(input.actualReceived ?? principal) : 0;
    if (input.receiveWalletId && !(received > 0)) throw new LedgerError('Số tiền thực nhận phải lớn hơn 0.');

    const db = getFirestore();
    const [a, b, c, d] = base(userId);
    const warnings: string[] = [];
    const debtRef = doc(collection(db, a, b, c, d, 'debts'));

    await runTransaction(db, async (tx) => {
      warnings.length = 0;
      const now = Timestamp.now();
      let wallet: { ref: any; data: SourceState } | null = null;
      if (input.receiveWalletId) {
        const ref = doc(db, a, b, c, d, 'sources', input.receiveWalletId);
        const snap = await tx.get(ref);
        if (!snap.exists()) throw new LedgerError('Không tìm thấy ví nhận tiền vay.');
        const data = snap.data() as SourceState;
        if (data.type === 'credit') throw new LedgerError('Không thể nhận tiền vay vào thẻ tín dụng — chọn ví thường.');
        wallet = { ref, data };
      }
      tx.set(debtRef, {
        lender, principal,
        remainingBalance: principal,
        interestRate: Number(input.interestRate) || 0,
        term: Number(input.term) || 0,
        dueDate: input.dueDate ? Timestamp.fromDate(new Date(input.dueDate)) : null,
        type: input.type || 'bank',
        status: 'active',
        note: input.note?.trim() || '',
        creditCardId: input.creditCardId || null,
        sourceAccountId: input.receiveWalletId || null,
        actualReceived: input.receiveWalletId ? received : null,
        // Giải ngân qua sổ cái (có dòng loan_disbursement) — báo cáo dòng tiền chỉ tính khoản này, tránh đếm trùng tiền vay đã ghi tay thành thu nhập
        disbursed: !!input.receiveWalletId,
        repaymentMethod: input.repaymentMethod || 'flexible',
        monthlyPayment: input.monthlyPayment ? Number(input.monthlyPayment) : null,
        createdAt: now, updatedAt: now
      });
      if (wallet) {
        tx.update(wallet.ref, { balance: (Number(wallet.data.balance) || 0) + received, updatedAt: now });
        tx.set(doc(collection(db, a, b, c, d, 'transactions')), {
          sourceId: input.receiveWalletId, amount: received, type: 'loan_disbursement',
          relatedId: debtRef.id, description: `Nhận tiền vay: ${lender} (${vnd(received)})`,
          timestamp: now, createdAt: now
        });
      }
    });
    return { id: debtRef.id, warnings };
  });

/** Xác nhận dự chi/dự thu thành khoản thực — một transaction (dùng bởi plans.ts). */
const fromPlan = (kind: EntryKind, collection: 'plannedExpenses' | 'plannedIncomes') =>
  (userId: string, plannedId: string, build: (planned: Data) => Data): Promise<LedgerResult> =>
    guard(async () => {
      const { ids, warnings } = await run(userId, [{ kind, op: 'add', planned: { collection, id: plannedId }, build }]);
      return { id: ids[0], warnings };
    });
export const addExpenseFromPlan = fromPlan('expense', 'plannedExpenses');
export const addIncomeFromPlan = fromPlan('income', 'plannedIncomes');
